import React, { useEffect } from 'react';
import { config as envConfig } from '../../config/environments';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Alert from '@mui/material/Alert';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ContactMail from '@mui/icons-material/ContactMail';
import Phone from '@mui/icons-material/Phone';
import Email from '@mui/icons-material/Email';
import Business from '@mui/icons-material/Business';
import ArrowForward from '@mui/icons-material/ArrowForward';
import Upload from '@mui/icons-material/Upload';
import Settings from '@mui/icons-material/Settings';
import VolumeUp from '@mui/icons-material/VolumeUp';

interface DemoContactRedirectProps {
  feature: 'upload' | 'settings' | 'audio';
  title?: string;
  description?: string;
}

export const DemoContactRedirect: React.FC<DemoContactRedirectProps> = ({
  feature,
  title,
  description,
}) => {
  const featureConfig = {
    upload: {
      icon: <Upload sx={{ fontSize: 48, color: 'primary.main' }} />,
      title: 'File Upload - Full Version Only',
      description: 'Upload and analyze your own call recordings with our full platform.',
      features: [
        'Bulk file upload support',
        'Automatic transcription and analysis',
        'Custom QA criteria application',
        'Real-time processing status',
      ],
    },
    settings: {
      icon: <Settings sx={{ fontSize: 48, color: 'primary.main' }} />,
      title: 'System Settings - Administrator Only',
      description: 'Configure analysis parameters, user permissions, and system integrations.',
      features: [
        'Custom QA criteria configuration',
        'User role and permission management',
        'System integration settings',
        'Audit and compliance controls',
      ],
    },
    audio: {
      icon: <VolumeUp sx={{ fontSize: 48, color: 'primary.main' }} />,
      title: 'Audio Playback - Full Version Only',
      description: 'Listen to original call recordings with synchronized transcripts.',
      features: [
        'High-quality audio playback',
        'Synchronized transcript highlighting',
        'Playback speed controls',
        'Bookmark and annotation tools',
      ],
    },
  };

  const config = featureConfig[feature];

  const handleContactUs = () => {
    const contactUrl = envConfig.demo?.contactRedirectUrl || envConfig.external.contactUrl;
    window.open(`${contactUrl}&feature=${feature}`, '_blank');
  };

  useEffect(() => {
    // Component mounted - user can manually click contact button
  }, []);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Card
        sx={{
          textAlign: 'center',
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          borderRadius: 2,
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box mb={3}>{config.icon}</Box>

          <Typography variant="h4" component="h1" gutterBottom color="primary">
            {title || config.title}
          </Typography>

          <Typography variant="h6" color="text.secondary" paragraph>
            {description || config.description}
          </Typography>

          <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
            <Typography variant="body2">
              <strong>Demo Limitation:</strong> This feature is not available in the demo version.
              Contact us to see the full platform with all capabilities.
            </Typography>
          </Alert>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            What you'll get in the full version:
          </Typography>

          <List sx={{ textAlign: 'left', mb: 3 }}>
            {config.features.map((featureItem, index) => (
              <ListItem key={index}>
                <ListItemIcon>
                  <ArrowForward color="primary" />
                </ListItemIcon>
                <ListItemText primary={featureItem} />
              </ListItem>
            ))}
          </List>

          <Box display="flex" gap={2} justifyContent="center" flexWrap="wrap">
            <Button
              variant="contained"
              size="large"
              startIcon={<ContactMail />}
              onClick={handleContactUs}
              sx={{ minWidth: 200 }}
            >
              Contact Us for Full Demo
            </Button>

            <Button variant="outlined" size="large" onClick={() => window.history.back()}>
              Back to Dashboard
            </Button>
          </Box>

          <Box mt={4} p={2} bgcolor="grey.50" borderRadius={1}>
            <Typography variant="h6" gutterBottom>
              Get in Touch
            </Typography>

            <Box display="flex" gap={3} justifyContent="center" flexWrap="wrap">
              <Box display="flex" alignItems="center" gap={1}>
                <Phone color="primary" />
                <Typography variant="body2">(555) 123-4567</Typography>
              </Box>

              <Box display="flex" alignItems="center" gap={1}>
                <Email color="primary" />
                <Typography variant="body2">demo@a3audio.com</Typography>
              </Box>

              <Box display="flex" alignItems="center" gap={1}>
                <Business color="primary" />
                <Typography variant="body2">Schedule a Call</Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};
